import type { MeView } from '@khaddi/contracts';
import { Avatar, Button, colors, Field, formatAppBuild, Icon, LayoutPicker, radii, Screen, Text, useAction, useApi, useAsync, useRouter, useSession } from '@khaddi/ui';
import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { InlineError, ListRow, Section } from '../components/bits';
import { useTabNav } from '../components/TabNav';
import { useFeatures } from '../state/appConfig';
import { APP_BUILD } from '../version';

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(-2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || 'KD';

export const AccountScreen = () => {
  const nav = useTabNav('account');
  const api = useApi();
  const { user, signOut, refreshUser } = useSession();
  const { navigate } = useRouter();
  const { delivery } = useFeatures();
  const me = useAsync(() => api.auth.me(), [api]);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name ?? '');
  const [saved, setSaved] = useState(false);

  const save = useAction(async () => {
    const view = await api.auth.updateProfile({ name: name.trim() });
    me.setData(view);
    await refreshUser();
    setEditing(false);
    setSaved(true);
  });

  if (!user) return null;
  const member = me.data?.member ?? null;

  return (
    <Screen nav={nav} maxWidth={720}>
      <View style={{ backgroundColor: colors.sky, paddingTop: 20, paddingHorizontal: 20, paddingBottom: 24, flexDirection: 'row', alignItems: 'center', gap: 14 }}>
        <Avatar initials={initialsOf(user.name)} background="#FFFFFF" size={60} />
        <View style={{ flex: 1 }}>
          <Text variant="title">{user.name}</Text>
          <Text variant="body">
            {user.phone ?? ''}
            {member ? ` · Hạng ${member.tier.name}` : ''}
          </Text>
        </View>
      </View>

      <View style={{ padding: 16, gap: 12 }}>
        <Section style={{ gap: 12 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text variant="heading">Thông tin cá nhân</Text>
            {!editing ? (
              <Button label="Sửa" icon="pencil" size="md" variant="ghost" onPress={() => { setName(user.name); setSaved(false); setEditing(true); }} />
            ) : null}
          </View>
          {editing ? (
            <>
              <Field label="Họ tên" value={name} onChangeText={setName} maxLength={80} autoComplete="name" onSubmitEditing={() => name.trim() && void save.run()} />
              <InlineError message={save.error} />
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <Button label="Hủy" size="md" variant="outline" onPress={() => setEditing(false)} style={{ flex: 1 }} />
                <Button label="Lưu" size="md" disabled={!name.trim()} loading={save.pending} onPress={() => void save.run()} style={{ flex: 1 }} />
              </View>
            </>
          ) : (
            <View style={{ gap: 8 }}>
              <InfoRow label="Họ tên" value={user.name} />
              <InfoRow label="Số điện thoại" value={user.phone ?? '—'} />
              {member ? <InfoRow label="Mã thành viên" value={member.memberCode} /> : null}
              {member ? <BirthdayRow birthday={member.birthday} onSaved={me.setData} onAlreadySet={() => void me.reload()} /> : null}
              {saved ? (
                <Text variant="caption" color={colors.greenText} style={{ fontWeight: '600' }}>
                  Đã lưu thông tin.
                </Text>
              ) : null}
            </View>
          )}
        </Section>

        <Section style={{ paddingVertical: 0 }}>
          <ListRow icon="receipt" title="Đơn hàng của tôi" onPress={() => navigate('/orders')} trailing={<Icon name="chevronRight" size={18} color={colors.textMuted} />} />
          <ListRow
            icon="gift"
            title="Thành viên & ưu đãi"
            subtitle={member ? `${member.points.toLocaleString('vi-VN')} điểm` : undefined}
            onPress={() => navigate('/rewards')}
            trailing={<Icon name="chevronRight" size={18} color={colors.textMuted} />}
          />
          <ListRow
            icon="user"
            title="Bảo mật tài khoản"
            subtitle={!user.phoneVerified ? 'Chưa xác minh số điện thoại' : !user.hasPin ? 'Chưa có mã PIN' : 'Đã xác minh · có mã PIN'}
            onPress={() => navigate('/verify')}
            trailing={<Icon name="chevronRight" size={18} color={colors.textMuted} />}
            last={!delivery}
          />
          {delivery ? (
            <ListRow
              icon="bike"
              title="Đăng ký làm shipper"
              subtitle="Giao trà cùng Khao Ddi, nhận tiền theo ngày"
              onPress={() => navigate('/shipper-signup')}
              trailing={<Icon name="chevronRight" size={18} color={colors.textMuted} />}
              last
            />
          ) : null}
        </Section>

        <LayoutPicker />

        <Section style={{ paddingVertical: 0 }}>
          <ListRow icon="logout" title="Đăng xuất" danger onPress={() => { signOut(); navigate('/', { replace: true }); }} last />
        </Section>
        <Text variant="caption" color={colors.textSubtle} style={{ textAlign: 'center' }}>
          {formatAppBuild(APP_BUILD)}
        </Text>
      </View>
    </Screen>
  );
};

/** "1995-10-25" → "25/10/1995". */
const formatBirthday = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const onlyDigits = (t: string) => t.replace(/\D/g, '');

/** Day/month/year fields → "YYYY-MM-DD" if it's a real past date, else null. */
const toIsoBirthday = (day: string, month: string, year: string): string | null => {
  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  if (!d || !m || year.length !== 4) return null;
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  if (y < 1900 || date.getTime() > Date.now()) return null;
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

/** The server's 400 when the customer already set a birthday once. */
const isAlreadySet = (e: unknown) => (e as { status?: number } | null)?.status === 400 && /đã được lưu/i.test(e instanceof Error ? e.message : '');

const COUNTER_HINT = 'Muốn sửa, nhờ nhân viên tại quầy';

/**
 * Birthday for the birthday gift. The customer may set it once; after that it's read-only and only
 * staff at the counter can change it. Three numeric fields rather than a native date picker: they
 * behave the same on every mobile browser and match the PIN/OTP fields.
 */
const BirthdayRow = ({ birthday, onSaved, onAlreadySet }: { birthday: string | null; onSaved: (view: MeView) => void; onAlreadySet: () => void }) => {
  const api = useApi();
  const { user } = useSession();
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [locked, setLocked] = useState(false);
  const iso = toIsoBirthday(day, month, year);

  const save = useAction(async (value: string) => {
    try {
      const view = await api.auth.updateProfile({ name: user?.name ?? '', birthday: value });
      onSaved(view);
      setOpen(false);
      setConfirming(false);
    } catch (e) {
      if (!isAlreadySet(e)) throw e;
      setLocked(true);
      setOpen(false);
      setConfirming(false);
      onAlreadySet();
    }
  });

  if (birthday || locked) {
    return (
      <View style={{ gap: 2 }}>
        <InfoRow label="Ngày sinh" value={birthday ? formatBirthday(birthday) : 'Đã lưu'} />
        <Text variant="caption" color={colors.textSubtle} style={{ textAlign: 'right' }}>
          {COUNTER_HINT}
        </Text>
      </View>
    );
  }

  if (!open) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Thêm ngày sinh để nhận quà sinh nhật"
        onPress={() => setOpen(true)}
        style={{ minHeight: 44, flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: colors.goldSoft, borderRadius: radii.md, paddingHorizontal: 12, paddingVertical: 10 }}
      >
        <Icon name="gift" size={20} color={colors.goldText} />
        <Text variant="body" color={colors.goldText} style={{ flex: 1, fontWeight: '600' }}>
          Thêm ngày sinh để nhận quà sinh nhật
        </Text>
        <Icon name="chevronRight" size={18} color={colors.goldText} />
      </Pressable>
    );
  }

  const incomplete = day.length > 0 && month.length > 0 && year.length === 4 && !iso;
  return (
    <View style={{ gap: 10, backgroundColor: colors.goldSoft, borderRadius: radii.md, padding: 12 }}>
      <Text variant="bodyStrong" color={colors.goldText}>
        Ngày sinh
      </Text>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <Field label="Ngày" placeholder="25" keyboardType="number-pad" inputMode="numeric" autoComplete="birthdate-day" maxLength={2} value={day} onChangeText={(t) => { setDay(onlyDigits(t)); setConfirming(false); }} />
        <Field label="Tháng" placeholder="10" keyboardType="number-pad" inputMode="numeric" autoComplete="birthdate-month" maxLength={2} value={month} onChangeText={(t) => { setMonth(onlyDigits(t)); setConfirming(false); }} />
        <Field label="Năm" placeholder="1995" keyboardType="number-pad" inputMode="numeric" autoComplete="birthdate-year" maxLength={4} value={year} onChangeText={(t) => { setYear(onlyDigits(t)); setConfirming(false); }} style={{ minWidth: 72 }} />
      </View>
      {incomplete ? <InlineError message="Ngày sinh chưa đúng, kiểm tra lại nhé." /> : null}
      {confirming && iso ? (
        <View accessibilityLiveRegion="polite">
          <Text variant="caption" color={colors.goldText} style={{ fontWeight: '700' }}>
            Ngày sinh chỉ đặt được một lần. Lưu {formatBirthday(iso)}?
          </Text>
        </View>
      ) : null}
      <InlineError message={save.error} />
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <Button
          label="Hủy"
          size="md"
          variant="outline"
          onPress={() => {
            if (confirming) setConfirming(false);
            else setOpen(false);
          }}
          style={{ flex: 1 }}
        />
        <Button
          label={confirming ? 'Xác nhận lưu' : 'Lưu'}
          size="md"
          disabled={!iso}
          loading={save.pending}
          onPress={() => (confirming && iso ? void save.run(iso) : setConfirming(true))}
          style={{ flex: 1 }}
        />
      </View>
    </View>
  );
};

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12 }}>
    <Text color={colors.textMuted} style={{ fontWeight: '400' }}>
      {label}
    </Text>
    <Text style={{ fontWeight: '600' }}>{value}</Text>
  </View>
);
